import { cloneDeep, isEqual } from 'lodash';
import { getFramework, type IFramework } from '@/framework';
import { TextRenderEvents, type TextRenderInvalidationEventPayload } from '@/framework/services/text-render';
import { getUtf8ByteLength, normalizeSelectionRange, rangeToUtf16, type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, type ILayerProps, type Optional } from '@/version-types';
import type { ClipboardPlugin } from '../clipboard/controller';
import { TextLayerEditorAdapter, VectorLayerEditorAdapter, type TextEditorAdapter } from './adapters';
import { Events } from './constants';
import { getTypingBoundary, TextEditorHistory, type TextEditorHistoryEntry, type UndoBoundary } from './history';
import { type EditorTextSelection, type TextEditorSession } from './types';
import { makeEditorTextSelection, setBaseOperationsDisabled } from './utils';

/**
 * 文本图层编辑器插件，负责文本编辑会话、选区、输入历史和字体子集加载。
 */
export class TextEditorPlugin {
    private readonly framework: IFramework;

    /**
     * 已注册的宿主适配器，按声明顺序选择第一个匹配项。
     */
    private readonly adapters: readonly TextEditorAdapter[];

    /**
     * 编辑操作的递增版本，用于阻止过期准备结果提交副作用。
     */
    private token = 0;

    /**
     * 当前文本编辑会话；为空表示未处于文本编辑态。
     */
    private session: TextEditorSession | null = null;

    /**
     * 当前编辑会话的撤销重做历史。
     */
    private history: TextEditorHistory | null = null;

    /**
     * 尚未执行排版的最新文本工作值。
     *
     * 连续输入会用同一会话的最新 `layer` 覆盖旧值，使尚未开始的排版天然 latest-only。
     * `sessionId` 用于阻止上一会话预约的任务写入新会话，`layer` 引用用于确认任务仍对应
     * session 当前持有的精确工作值。
     */
    private pendingLayoutRequest: { sessionId: number; layer: ILayerProps } | null = null;

    /**
     * 已预约、尚未进入宏任务阶段的动画帧 ID；0 表示没有预约。
     */
    private layoutFrame = 0;

    /**
     * 动画帧后用于实际执行排版的宏任务 ID；0 表示没有预约。
     */
    private layoutTimer = 0;

    /**
     * 进行中的退出事务
     *
     * session 在事务开始时立即清除，Adapter 通过调用栈持有退出所需的不可变 context；该 Promise 是
     * Manager 唯一保留的 pending 状态，用于让并发 exit 和后续 enter 等待同一次 finalization。
     */
    private exitPromise: Promise<void> | null = null;

    /**
     * 创建 Text Editor manager，并订阅宿主 Renderer 的幂等失效通知。
     *
     * @param identity - 当前编辑器实例标识。
     * @returns TextEditorPlugin 实例。
     */
    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.adapters = [new TextLayerEditorAdapter(this.framework), new VectorLayerEditorAdapter(this.framework)];
        this.framework.service.events.on(TextRenderEvents.Invalidation, this.handleTextRenderInvalidated);
    }

    /**
     * 将当前选区样式同步保存到内部剪贴板，系统纯文本由复制事件写入
     *
     * @param range - 从 textarea 读取的 UTF-8 字节选区
     */
    copySelection(range: TextSelectionRange): void {
        const session = this.session;
        if (!session) return;

        const text = session.adapter.getText(session.editingLayer);
        const [start, end] = rangeToUtf16(text, range);
        const selectedText = text.slice(start, end);
        if (!selectedText) return;

        const layer = session.adapter.createClipboardLayer?.(session.editingLayer, range);
        const { frame, registration } = this.framework.service;
        const frameId = frame.getLayerFrameId(session.editingLayer.id) ?? frame.getActiveFrameId();
        registration
            .getInstance<ClipboardPlugin>('ClipboardPlugin')
            .setTextSelectionClipboardData(
                selectedText,
                layer ? { layer, extraInfo: frame.getExtraInfoByFrameId(frameId) } : null,
            );
    }

    /**
     * 获取当前文本编辑会话。
     *
     * @returns 当前文本编辑会话
     */
    getSession(): TextEditorSession | null {
        return this.session;
    }

    /**
     * 获取正在编辑的文本图层。
     *
     * @returns 正在编辑的文本图层
     */
    getEditingLayer() {
        if (!this.session) return null;
        return this.session.editingLayer;
    }

    /**
     * 获取当前文本选区范围。
     *
     * @returns 当前文本选区范围
     */
    getSelectionRange() {
        if (!this.session) return null;
        return normalizeSelectionRange(this.session.selection);
    }

    /**
     * 获取当前文本 range 命中的富文本共享属性状态。
     *
     * @returns 当前 range 属性状态；未编辑时返回 null
     */
    resolveRangeAttrsState() {
        if (!this.session) return null;
        return this.session.adapter.resolveRangeAttrsState?.(this.session.editingLayer, this.session.selection) ?? null;
    }

    /**
     * 监听文本编辑会话变化。
     *
     * @param callback - 文本编辑会话变化回调
     * @returns 取消监听函数
     */
    onSessionChange(callback: (session: TextEditorSession | null) => void) {
        const handler = this.framework.service.events.on(Events.SessionChange, callback);
        return () => this.framework.service.events.off(handler);
    }

    /**
     * 更新当前文本选区。
     *
     * @param range - 新的文本选区范围
     */
    setSelection(range: TextSelectionRange | EditorTextSelection) {
        const session = this.session;
        if (!session) return;

        const selection = makeEditorTextSelection(session.adapter.getText(session.editingLayer), range);
        if (isEqual(session.selection, selection)) return;

        this.history?.seal();
        this.history?.replaceCurrent({ editingLayer: session.editingLayer, selection });

        this.patchSession({ selection });
    }

    /**
     * 同步外部更新后的图层数据到当前编辑会话。
     *
     * 外部更新已经提供了新的权威工作值，必须先取消由旧 session 图层预约的排版，避免旧任务随后
     * 覆盖同步结果。Adapter 会基于当前 session 合并宿主特有的临时数据，并重新解析可用几何。
     *
     * @param layer - 外部更新后的图层数据。
     * @returns 图层属于当前编辑会话且同步成功时返回 true。
     */
    syncLayer(layer: ILayerProps) {
        const session = this.session;
        if (!session || session.editingLayer.id !== layer.id || !session.adapter.matches(layer)) return false;

        this.cancelPendingLayout();

        const state = session.adapter.syncLayer(layer, {
            editingLayer: session.editingLayer,
            snapshot: session.snapshot,
        });
        const selection = makeEditorTextSelection(session.adapter.getText(state.editingLayer), session.selection);

        this.patchSession({ ...state, selection });
        return true;
    }

    /**
     * 更新当前编辑图层的文本内容和选区。
     *
     * 输入热路径只同步更新工作值、选区和撤销历史，并把最新数据写入画布；这里不执行全文排版。
     * 排版通过 `scheduleLayout()` 延后，相同会话中的连续输入只保留最后一个工作值。需要立即使用
     * 几何的调用方必须先调用 `flushLayout()`。
     *
     * @param text - 新的文本内容。
     * @param selection - 新的文本选区；省略时沿用并归一化当前选区。
     * @param boundary - 撤销边界；省略时根据前后文本推断，null 表示本次不建立边界。
     * @returns 已同步写入但可能尚未排版的最新图层；无编辑会话时返回 null。
     */
    updateText(text: string, selection?: Optional<EditorTextSelection>, boundary?: Optional<UndoBoundary>) {
        const session = this.session;
        if (!session) return null;

        const previousText = session.adapter.getText(session.editingLayer);
        if (previousText === text) {
            if (selection) this.setSelection(selection);
            return session.editingLayer;
        }

        const editingLayer = session.adapter.applyText(session.editingLayer, text);

        const nextSelection = makeEditorTextSelection(text, selection ?? session.selection);
        const nextBoundary = boundary === undefined ? getTypingBoundary(previousText, text) : boundary;

        if (nextBoundary) {
            this.history?.push({ editingLayer, selection: nextSelection }, nextBoundary);
        }

        session.adapter.updateEditingData(editingLayer);
        this.patchSession({ editingLayer, selection: nextSelection });
        this.scheduleLayout(editingLayer);

        return editingLayer;
    }

    /**
     * 提交输入法组合输入，并使用 IME 撤销边界收束历史。
     *
     * IME composition 结束后，历史项必须引用最终排版工作值，因此先写入组合文本并同步 flush
     * 尚未执行的排版，再将整个组合过程封成单一的 `ime` 撤销边界。
     *
     * @param text - 输入法组合后的文本内容。
     * @param selection - 输入法组合后的文本选区。
     * @returns 会话与历史都有效且成功收束时返回 true。
     */
    flushComposition(text: string, selection: EditorTextSelection) {
        this.updateText(text, selection, null);
        this.flushLayout();

        const session = this.session;
        const history = this.history;
        if (!session || !history) return false;

        const current = history.getCurrent();
        if (!current) return false;

        const next: TextEditorHistoryEntry = {
            editingLayer: session.editingLayer,
            selection: session.selection,
        };
        if (isEqual(current.editingLayer, next.editingLayer)) {
            history.replaceCurrent(next);
            history.seal();
        } else {
            history.push(next, 'ime');
        }

        return true;
    }

    /**
     * 撤销当前编辑会话中的上一步文本修改。
     *
     * 撤销前先收敛 pending layout，确保当前历史位置、session snapshot 和画布几何描述同一份工作值；
     * 随后再对历史目标执行一次同步解析并发布。
     *
     * @returns 是否撤销成功
     */
    undo() {
        this.flushLayout();
        const session = this.session;
        const history = this.history;
        if (!session || !history) return false;

        const prev = history.undo();
        if (!prev) return false;

        const state = session.adapter.resolveEditingState(prev.editingLayer, session.snapshot);
        const selection = makeEditorTextSelection(session.adapter.getText(state.editingLayer), prev.selection);

        // 必须先让 session 持有撤销后的文字、快照和选区，再写入画布：updateEditingLayer 内的
        // Frame 更新会同步触发 useLifecycle -> syncLayer，回调会读取此处的当前 session。
        // 原本没有 textInfo 的 Vector 撤销到空文字时，Adapter 会从持久化数据摘除 textInfo；
        // syncLayer 因而需要从 session 补回仅供编辑使用的空候选。若 session 仍是撤销前的值，
        // 回调就会补回旧文字并发出 refresh，覆盖已经发出的清空 Preview，留下“光标归零但文字残留”。
        this.patchSession({ ...state, selection });
        session.adapter.updateEditingLayer(state.editingLayer, state.snapshot);

        return true;
    }

    /**
     * 重做当前编辑会话中的下一步文本修改。
     *
     * 重做前先收敛 pending layout，避免尚未发布的输入排版在重做结果之后执行并覆盖几何；
     * 随后再对历史目标执行一次同步解析并发布。
     *
     * @returns 是否重做成功
     */
    redo() {
        this.flushLayout();
        const session = this.session;
        const history = this.history;
        if (!session || !history) return false;

        const next = history.redo();
        if (!next) return false;

        const state = session.adapter.resolveEditingState(next.editingLayer, session.snapshot);
        const selection = makeEditorTextSelection(session.adapter.getText(state.editingLayer), next.selection);

        // 重做也可能恢复到“删除全部文字”的历史节点，必须和 undo 一样先发布完整目标会话。
        // 这样 Frame 的同步回调补候选时读到的就是重做后的空文字，并且不会用重做前的选区
        // 覆盖目标选区；画布写入结束后也不再覆盖回调已经同步好的会话状态。
        this.patchSession({ ...state, selection });
        session.adapter.updateEditingLayer(state.editingLayer, state.snapshot);

        return true;
    }

    /**
     * 进入文本图层编辑态。
     *
     * @param layerOrID - 文本图层或图层ID
     * @param options - 进入编辑态选项
     *  - options.selectAll - 是否选择所有文本；默认全选尚未修改文案的文本图层
     * @returns 是否进入成功
     */
    async enter(layerOrID: string | ILayerProps, options: { selectAll?: boolean } = {}) {
        let layer = typeof layerOrID === 'string' ? this.framework.service.flatLayer.getLayer(layerOrID) : layerOrID;
        let adapter = layer ? this.resolveAdapter(layer) : null;

        // 双击和直接调用 enter 都必须遵守宿主的文字编辑规则：Vector behavior 在无限画布下返回 false，
        // Text Layer 仍可编辑。只拒绝明确的 false，未声明该可选行为的宿主沿用 adapter 匹配结果。
        // 校验放在 exit 前，避免被禁用的目标请求中断当前文字会话或创建临时 textInfo 候选。
        // 该能力配置在运行时保持不变，只需在入口校验一次。
        if (
            !layer ||
            !adapter ||
            layer.lock ||
            this.framework.service.editor.readonly ||
            this.framework.service.behavior.get(layer)?.isTextEditable?.() === false
        ) {
            return false;
        }
        const token = ++this.token;

        // 这里只关闭已有会话，不使当前 enter 的 token 失效。
        await this.exit({ cancelPendingEnter: false });
        if (this.session || token !== this.token) return false;

        // exit 是异步过程，期间目标图层状态可能变化，因此需要重新获取并校验。
        layer = this.framework.service.flatLayer.getLayer(layer.id, true);
        adapter = layer ? this.resolveAdapter(layer) : null;
        if (!layer || !adapter || layer.lock) return false;

        // 字体准备会原地补充 runtime；进入校验必须使用准备前的稳定基线。
        const enterBaseline = cloneDeep(layer);
        const state = await adapter.prepareEnter(layer);
        if (!state || token !== this.token || this.session) return false;

        // prepareEnter 可能包含异步操作，期间协作或外部 API 可能更新目标图层。
        // 提交前重新读取并比较，避免基于旧图层生成的编辑状态覆盖最新数据。
        const latest = this.framework.service.flatLayer.getLayer(layer.id, true);
        if (!latest || !isEqual(latest, enterBaseline)) return false;

        const originLayer = layer;
        adapter.commitEnter(state, originLayer);

        const latestText = adapter.getText(state.editingLayer);
        const textLength = getUtf8ByteLength(latestText);
        const selectAll = options.selectAll ?? (layer.layerType === LayerType.TEXT_LAYER && !layer.extraInfo?.modified);
        const range = selectAll ? { start: 0, end: textLength } : undefined;
        const selection = makeEditorTextSelection(latestText, range);

        const session: TextEditorSession = {
            id: Date.now(),
            ...state,
            originLayer,
            adapter,
            selection,
        };

        setBaseOperationsDisabled(this.framework, true);
        this.setSession(session);
        this.history = new TextEditorHistory({ editingLayer: session.editingLayer, selection });

        return true;
    }

    /**
     * 退出文本编辑态，并将最终图层数据写回历史。
     *
     * 退出事务先收敛最后一次输入并立即清除公开 session，再由 Adapter 独立完成宿主准备与提交。
     * 并发 exit 复用进行中的 Promise，enter 也会等待该事务结束，避免新旧编辑会话交叠。
     *
     * @param options - 退出编辑态选项；cancelPendingEnter 表示是否取消尚未建立 session 的 enter。
     * @returns 无返回值。
     */
    async exit(options: { cancelPendingEnter: boolean } = { cancelPendingEnter: true }): Promise<void> {
        if (this.exitPromise) return this.exitPromise;

        const running = this.performExit(options);
        this.exitPromise = running.finally(() => {
            this.exitPromise = null;
        });
        return this.exitPromise;
    }

    /**
     * 执行一次退出准备与提交。
     *
     * @param options - 与 exit() 相同的退出选项。
     * @returns 无返回值。
     */
    private async performExit(options: { cancelPendingEnter: boolean }): Promise<void> {
        // exit() 可能在 enter() 尚未建立 session 时调用，此时也必须使挂起的 enter() 失效。
        if (options.cancelPendingEnter) this.token++;

        const session = this.flushLayout();
        const token = this.token;
        if (!session) return;

        this.cancelPendingLayout();
        this.setSession(null);

        let finalized = false;
        try {
            finalized = await session.adapter.finalizeExit(session);
        } catch (error) {
            console.error('Text Editor finalization failed:', error);
        }

        if (finalized || token !== this.token) {
            this.history = null;
            setBaseOperationsDisabled(this.framework, false);
            return;
        }

        const latest = this.framework.service.flatLayer.getLayer(session.editingLayer.id, true);
        if (!latest || latest.lock) {
            this.history = null;
            setBaseOperationsDisabled(this.framework, false);
            return;
        }

        const state = session.adapter.syncLayer(latest, {
            editingLayer: session.editingLayer,
            snapshot: session.snapshot,
        });
        const selection = makeEditorTextSelection(session.adapter.getText(state.editingLayer), session.selection);
        this.setSession({ ...session, ...state, selection });
    }

    /**
     * 为宿主图层选择直接注册的编辑适配器。
     *
     * Text 与 Vector 各自按 layer type 精确匹配，不引入 adapter factory 或第三种宿主协议。
     *
     * @param layer - 待编辑的宿主图层。
     * @returns 匹配的适配器；无宿主支持时返回 null。
     */
    private resolveAdapter(layer: ILayerProps): TextEditorAdapter | null {
        return this.adapters.find((adapter) => adapter.matches(layer)) ?? null;
    }

    /**
     * 设置当前编辑会话并通知订阅方。
     *
     * @param session - 新的编辑会话
     * @returns 无返回值。
     */
    private setSession(session: TextEditorSession | null) {
        this.session = session;
        this.framework.service.events.emit(Events.SessionChange, this.session);
    }

    /**
     * 使用当前最新 session 处理宿主 Renderer 的幂等失效通知。
     *
     * 通知不携带异步 snapshot，因此旧通知也只会触发对当前最新工作值的无害重解析。
     * 回调开始时捕获精确 session 对象，并在每个可能触发同步外部更新的边界后校验 identity；
     * 这里不使用 enter/exit token，因为退出准备失败后原 session 仍然有效。
     *
     * adapter 的 `invalidation` 原因表示保留已经开始的 drawable 交接；Text Render Invalidation
     * 只让最新输入重算，不是新一轮用户编辑，因此不得取消该交接等待。
     * 若当前还有普通输入预约的 layout，则先取消预约，再以 session 最新工作值同步重算，避免同一
     * 输入被失效回调和延后任务重复发布。
     *
     * @param layerId - 当前 Text Render 结果已失效的真实宿主图层 ID。
     * @returns 无返回值。
     */
    private readonly handleTextRenderInvalidated = ({ layerId }: TextRenderInvalidationEventPayload): void => {
        const capturedSession = this.session;
        if (
            !capturedSession ||
            capturedSession.editingLayer.id !== layerId ||
            this.resolveAdapter(capturedSession.editingLayer) !== capturedSession.adapter
        ) {
            return;
        }

        this.cancelPendingLayout();
        // 失效事件不携带旧 snapshot；始终以当前 session 的最新 editingLayer 重算。
        const state = capturedSession.adapter.resolveEditingState(
            capturedSession.editingLayer,
            capturedSession.snapshot,
        );
        if (this.session !== capturedSession) return;

        capturedSession.adapter.updateEditingLayer(state.editingLayer, state.snapshot, 'invalidation');
        if (this.session !== capturedSession) return;
        this.patchSession(state);
    };

    /**
     * 合并更新当前编辑会话。
     *
     * @param patch - 要合并的更新
     */
    private patchSession(patch: Partial<TextEditorSession>) {
        if (!this.session) return;
        this.setSession({ ...this.session, ...patch });
    }

    /**
     * 同步执行并发布当前会话尚未处理的最新排版工作值。
     *
     * 方法先取消 rAF/timer，取得 pending 所有权并清空队列，再用 session ID 与工作值引用进行双重
     * 校验。Adapter 解析或发布期间可能同步触发外部更新，因此每个副作用边界后都重新检查 session
     * identity，过期结果不能写回后来建立的状态。
     *
     * @returns 收敛后的当前会话；任务过期或当前无会话时返回当时最新的 session/null。
     */
    flushLayout(): TextEditorSession | null {
        const pending = this.pendingLayoutRequest;
        if (!pending) return this.session;

        this.cancelLayoutCallbacks();
        this.pendingLayoutRequest = null;

        const session = this.session;
        if (!session || session.id !== pending.sessionId || session.editingLayer !== pending.layer) return session;

        const state = session.adapter.resolveEditingState(pending.layer, session.snapshot);
        if (this.session !== session) return this.session;

        session.adapter.updateEditingLayer(state.editingLayer, state.snapshot);
        if (this.session !== session) return this.session;

        this.patchSession(state);
        return this.session;
    }

    /**
     * 为当前会话预约一次 latest-only 排版。
     *
     * 每次调用都会覆盖 `pendingLayoutRequest`，但同一时间只保留一组 rAF 和 timer。rAF 让当前输入与选区
     * 状态先进入浏览器绘制阶段，随后的宏任务再执行同步 layout，避免把长文本排版塞进 input handler
     * 或当前动画帧。同步 layout 已开始后无法取消，latest-only 只合并尚未开始的任务。
     *
     * @param layer - 已写入 session、等待排版的精确工作值。
     * @returns 无返回值。
     */
    private scheduleLayout(layer: ILayerProps): void {
        const session = this.session;
        if (!session) return;

        this.pendingLayoutRequest = { sessionId: session.id, layer };
        if (this.layoutFrame || this.layoutTimer) return;

        const requestFrame =
            globalThis.requestAnimationFrame ??
            ((callback: FrameRequestCallback) => window.setTimeout(() => callback(performance.now()), 0));

        this.layoutFrame = requestFrame(() => {
            this.layoutFrame = 0;
            this.layoutTimer = window.setTimeout(() => {
                this.layoutTimer = 0;
                this.flushLayout();
            }, 0);
        });
    }

    /**
     * 取消排版回调并丢弃 pending 工作值。
     *
     * 用于外部同步、Renderer 失效和会话结束等已经取得新状态所有权的边界，防止旧输入稍后发布。
     *
     * @returns 无返回值。
     */
    private cancelPendingLayout(): void {
        this.cancelLayoutCallbacks();
        this.pendingLayoutRequest = null;
    }

    /**
     * 取消当前预约的 rAF 与宏任务，但不修改 `pendingLayoutRequest`。
     *
     * `flushLayout()` 使用这一较低层方法接管 pending 工作值后立即同步执行；完整取消则由
     * `cancelPendingLayout()` 在此基础上额外清空 pending。
     *
     * @returns 无返回值。
     */
    private cancelLayoutCallbacks(): void {
        if (this.layoutFrame) {
            const cancelFrame = globalThis.cancelAnimationFrame ?? window.clearTimeout;
            cancelFrame(this.layoutFrame);
        }

        if (this.layoutTimer) window.clearTimeout(this.layoutTimer);

        this.layoutFrame = 0;
        this.layoutTimer = 0;
    }
}
